import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, MemoryRouter } from "react-router-dom";
import { ThemeProvider, ToastProvider, bindMutationCache } from "@guestpilot/ui-kit";
import { App } from "./App";
import { ApiProblemError, configureApi } from "./api/client";
import { I18nProvider, useT } from "./i18n";
import { onThemeChange, type TeamsInfo, type ThemeName } from "./teams";

export function makeQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        // a 4xx is an answer, not a transient failure
        retry: (count, err) => !(err instanceof ApiProblemError && err.status < 500) && count < 2,
      },
    },
  });
  bindMutationCache(client.getMutationCache()); // a button that starts a request is busy until it ends
  return client;
}

function ToastHost({ children }: { children: React.ReactNode }) {
  const t = useT();
  return <ToastProvider closeLabel={t("common:close")}>{children}</ToastProvider>;
}

/** Providers in one place: query cache, theme (follows Teams, 04 §9), language, toasts, router. */
export function Root({
  teams,
  router = "browser",
  initialPath,
  client,
}: {
  teams: TeamsInfo;
  router?: "browser" | "memory";
  initialPath?: string;
  client?: QueryClient;
}) {
  const [qc] = useState(() => client ?? makeQueryClient());
  const [theme, setTheme] = useState<ThemeName>(teams.theme);
  useEffect(() => {
    onThemeChange(setTheme);
  }, []);
  useEffect(() => {
    configureApi({ locale: teams.locale });
  }, [teams.locale]);
  const Router = router === "memory" ? MemoryRouter : BrowserRouter;
  const routerProps = router === "memory" ? { initialEntries: [initialPath ?? "/"] } : {};
  return (
    <QueryClientProvider client={qc}>
      <ThemeProvider theme={theme}>
        <I18nProvider locale={teams.locale}>
          <ToastHost>
            <Router {...routerProps}>
              <App teams={teams} />
            </Router>
          </ToastHost>
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
